// -------------------------------
// ESIC BENEFIT INTERFACES
// -------------------------------
export interface PendingEsicBenefit {
    benefit_name?: string;
    description?: string;
}

export interface PendingDeleteEsicBenefit {
    awaitingName?: boolean;
}

// -------------------------------
// CREATE ESIC BENEFIT — START
// -------------------------------
export const startCreateEsicBenefitFlow = (
    setPending: (data: PendingEsicBenefit | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void,
    nameFromIntent?: string
) => {
    if (nameFromIntent) {
        setPending({ benefit_name: nameFromIntent });

        pushAIChat(`Please provide the description for "${nameFromIntent}".`);
        speak(`Please provide the description for ${nameFromIntent}.`);
        return;
    }

    setPending({});
    pushAIChat("Sure! What should the ESIC benefit be called?");
    speak("What should the ESIC benefit be called?");
};

// -------------------------------
// CREATE ESIC BENEFIT — PROCESS
// -------------------------------
export const processPendingEsicBenefit = (
    text: string,
    pending: PendingEsicBenefit,
    setPending: (data: PendingEsicBenefit | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void
) => {

    // STEP 1 — BENEFIT NAME
    if (!pending.benefit_name) {
        const cleanName = text.replace(/[^a-zA-Z0-9 ]/g, "").trim();

        if (!cleanName) {
            pushAIChat("Please provide a valid benefit name.");
            speak("Please provide a valid benefit name.");
            return;
        }

        setPending({ ...pending, benefit_name: cleanName });

        pushAIChat("Please provide the description for this benefit.");
        speak("Please provide the description.");
        return;
    }

    // STEP 2 — DESCRIPTION (FINAL)
    if (!pending.description) {
        const description = text.trim();

        if (!description) {
            pushAIChat("Please provide a valid description.");
            speak("Please provide a valid description.");
            return;
        }

        pushAIChat(`Perfect! Creating ESIC benefit "${pending.benefit_name}"...`);
        speak(`Creating ESIC benefit ${pending.benefit_name}.`);

        window.dispatchEvent(
            new CustomEvent("ai-create-esic-benefit", {
                detail: {
                    benefit_name: pending.benefit_name,
                    description
                }
            })
        );

        setPending(null);
        return;
    }
};

// -------------------------------
// DELETE ESIC BENEFIT — START
// -------------------------------
export const startDeleteEsicBenefitFlow = (
    setPendingDelete: (data: PendingDeleteEsicBenefit | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void,
    nameFromIntent?: string
) => {
    if (nameFromIntent) {
        pushAIChat(`Deleting ESIC benefit "${nameFromIntent}"...`);
        speak(`Deleting ESIC benefit ${nameFromIntent}.`);

        window.dispatchEvent(
            new CustomEvent("ai-delete-esic-benefit", {
                detail: { name: nameFromIntent }
            })
        );
        return;
    }

    setPendingDelete({ awaitingName: true });
    pushAIChat("Which ESIC benefit would you like to delete?");
    speak("Which ESIC benefit would you like to delete?");
};

// -------------------------------
// DELETE ESIC BENEFIT — PROCESS
// -------------------------------
export const processPendingDeleteEsicBenefit = (
    text: string,
    pending: PendingDeleteEsicBenefit,
    setPendingDelete: (data: PendingDeleteEsicBenefit | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void
) => {
    if (!pending.awaitingName) return;

    const cleanName = text.replace(/[^a-zA-Z0-9 ]/g, "").trim();

    if (!cleanName) {
        pushAIChat("Please provide a valid benefit name.");
        speak("Please provide a valid benefit name.");
        return;
    }

    pushAIChat(`Deleting ESIC benefit "${cleanName}"...`);
    speak(`Deleting ESIC benefit ${cleanName}.`);

    window.dispatchEvent(
        new CustomEvent("ai-delete-esic-benefit", {
            detail: { name: cleanName }
        })
    );

    setPendingDelete(null);
};